import { createContext, useCallback, useContext, useRef, useState } from 'react'

// Bumped by TabKeepAlive each time a kept-alive tab is shown again. Outside a
// tab it stays 0, so these hooks behave like their plain React counterparts.
export const TabVisitContext = createContext(0)

// False while TabKeepAlive keeps the enclosing tab hidden.
export const TabActiveContext = createContext(true)

/**
 * Whether the enclosing tab is the one showing. <Activity> hides a tab's own DOM
 * but not what it portals to the body, and a hidden tab runs no effects, so a
 * floater opened in it can't close itself: the shared floater contents in
 * components/ui render nothing instead, and come back when the tab does.
 */
export function useTabActive(): boolean {
  return useContext(TabActiveContext)
}

/** Which visit to the enclosing tab this is — changes each time the user comes back to it. */
export function useTabVisit(): number {
  return useContext(TabVisitContext)
}

/**
 * `useState` that resets to `initial` each time the user comes back to the tab
 * — the per-visit lifetime a remount used to give state seeded from the URL
 * or storage.
 */
export function useVisitState<T>(initial: T | (() => T)) {
  const visit = useTabVisit()
  const [state, setState] = useState(initial)
  const [prevVisit, setPrevVisit] = useState(visit)
  if (visit !== prevVisit) {
    setPrevVisit(visit)
    setState(typeof initial === 'function' ? (initial as () => T)() : initial)
  }
  return [state, setState] as const
}

/**
 * A "once" guard that re-arms on every visit to the tab: returns true the first
 * time it's called in a visit, false after. Replaces a `useRef(false)` guard
 * that counted on the page remounting.
 */
export function useOncePerVisit(): () => boolean {
  const visit = useTabVisit()
  const done = useRef<number | null>(null)
  return useCallback(() => {
    if (done.current === visit) return false
    done.current = visit
    return true
  }, [visit])
}
